import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";

interface VerifiedCompany {
  id: number;
  name: string;
  website_url: string;
  registration_code: string;
  deposit_amount: string;
  game_types: string;
  features: string;
  content: string;
  image_url: string;
  category: string;
  is_active: boolean;
  sort_order: number;
  description: string;
  created_at: string;
}

const GAME_TYPE_OPTIONS = [
  "스포츠",
  "카지노",
  "슬롯",
  "바카라",
  "홀덤",
  "토토",
  "미니게임",
  "그래프",
  "파워볼",
  "E스포츠",
];

const CATEGORY_OPTIONS = ["일반", "카지노", "슬롯", "스포츠", "토토", "바카라", "홀덤"];

export default function AdminVerifiedPage() {
  const [companies, setCompanies] = useState<VerifiedCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editCompany, setEditCompany] = useState<VerifiedCompany | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingContent, setUploadingContent] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const contentFileInputRef = useRef<HTMLInputElement>(null);
  const contentTextareaRef = useRef<HTMLTextAreaElement>(null);

  const [name, setName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [registrationCode, setRegistrationCode] = useState("");
  const [depositAmount, setDepositAmount] = useState("");
  const [gameTypes, setGameTypes] = useState<string[]>([]);
  const [features, setFeatures] = useState("");
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [category, setCategory] = useState("일반");
  const [sortOrder, setSortOrder] = useState("0");
  const [previewUrl, setPreviewUrl] = useState("");

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("verified_companies")
        .select("*")
        .order("sort_order", { ascending: true });

      if (error) throw error;
      setCompanies(data || []);
    } catch {
      setMessage({ type: "error", text: "보증업체 목록을 불러오지 못했습니다" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const resetForm = useCallback(() => {
    setName("");
    setWebsiteUrl("");
    setRegistrationCode("");
    setDepositAmount("");
    setGameTypes([]);
    setFeatures("");
    setContent("");
    setImageUrl("");
    setCategory("일반");
    setSortOrder("0");
    setPreviewUrl("");
    setEditCompany(null);
    setShowForm(false);
  }, []);

  const openCreateForm = useCallback(() => {
    resetForm();
    setShowForm(true);
  }, [resetForm]);

  const openEditForm = useCallback((company: VerifiedCompany) => {
    setEditCompany(company);
    setName(company.name);
    setWebsiteUrl(company.website_url || "");
    setRegistrationCode(company.registration_code || "");
    setDepositAmount(company.deposit_amount || "");
    setGameTypes(company.game_types ? company.game_types.split(",").map((g) => g.trim()).filter(Boolean) : []);
    setFeatures(company.features || "");
    setContent(company.content || "");
    setImageUrl(company.image_url || "");
    setCategory(company.category || "일반");
    setSortOrder(String(company.sort_order));
    setPreviewUrl(company.image_url || "");
    setShowForm(true);
  }, []);

  const handleImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: "error", text: "파일 크기는 5MB 이하여야 합니다" });
      return;
    }

    setUploading(true);
    setMessage(null);

    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `verified/${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("admin-assets")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("admin-assets").getPublicUrl(fileName);
      const publicUrl = urlData.publicUrl;

      setImageUrl(publicUrl);
      setPreviewUrl(publicUrl);
      setMessage({ type: "success", text: "썸네일 이미지 업로드 완료!" });
    } catch {
      setMessage({ type: "error", text: "이미지 업로드에 실패했습니다" });
    } finally {
      setUploading(false);
    }
  }, []);

  const handleContentImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: "error", text: "파일 크기는 5MB 이하여야 합니다" });
      return;
    }

    setUploadingContent(true);
    setMessage(null);

    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `verified/content-${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("admin-assets")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("admin-assets").getPublicUrl(fileName);
      const publicUrl = urlData.publicUrl;

      const imgTag = `\n<img src="${publicUrl}" alt="본문 이미지" class="w-full rounded-lg my-3" />\n`;
      const textarea = contentTextareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newContent = content.slice(0, start) + imgTag + content.slice(end);
        setContent(newContent);
        setTimeout(() => {
          textarea.selectionStart = start + imgTag.length;
          textarea.selectionEnd = start + imgTag.length;
          textarea.focus();
        }, 0);
      } else {
        setContent((prev) => prev + imgTag);
      }

      setMessage({ type: "success", text: "본문 이미지 업로드 완료!" });
    } catch {
      setMessage({ type: "error", text: "이미지 업로드에 실패했습니다" });
    } finally {
      setUploadingContent(false);
      if (contentFileInputRef.current) {
        contentFileInputRef.current.value = "";
      }
    }
  }, [content]);

  const toggleGameType = useCallback((gameType: string) => {
    setGameTypes((prev) =>
      prev.includes(gameType) ? prev.filter((g) => g !== gameType) : [...prev, gameType],
    );
  }, []);

  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      setMessage({ type: "error", text: "사이트 이름을 입력해주세요" });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const payload = {
        name: name.trim(),
        website_url: websiteUrl.trim() || null,
        registration_code: registrationCode.trim() || null,
        deposit_amount: depositAmount.trim() || null,
        game_types: gameTypes.length > 0 ? gameTypes.join(", ") : null,
        features: features.trim() || null,
        content: content.trim() || null,
        image_url: imageUrl.trim() || null,
        category: category.trim() || "일반",
        sort_order: parseInt(sortOrder, 10) || 0,
      };

      if (editCompany) {
        const { error } = await supabase
          .from("verified_companies")
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq("id", editCompany.id);
        if (error) throw error;
        setMessage({ type: "success", text: "보증업체 정보가 수정되었습니다" });
      } else {
        const { error } = await supabase.from("verified_companies").insert(payload);
        if (error) throw error;
        setMessage({ type: "success", text: "보증업체가 등록되었습니다" });
      }

      resetForm();
      fetchCompanies();
    } catch {
      setMessage({ type: "error", text: "저장에 실패했습니다" });
    } finally {
      setSaving(false);
    }
  }, [name, websiteUrl, registrationCode, depositAmount, gameTypes, features, content, imageUrl, category, sortOrder, editCompany, resetForm, fetchCompanies]);

  const handleToggleActive = useCallback(async (company: VerifiedCompany) => {
    try {
      const { error } = await supabase
        .from("verified_companies")
        .update({ is_active: !company.is_active, updated_at: new Date().toISOString() })
        .eq("id", company.id);
      if (error) throw error;

      setCompanies((prev) =>
        prev.map((c) => (c.id === company.id ? { ...c, is_active: !c.is_active } : c)),
      );
    } catch {
      setMessage({ type: "error", text: "상태 변경에 실패했습니다" });
    }
  }, []);

  const handleDelete = useCallback(async (company: VerifiedCompany) => {
    try {
      const { error } = await supabase.from("verified_companies").delete().eq("id", company.id);
      if (error) throw error;

      setCompanies((prev) => prev.filter((c) => c.id !== company.id));
      setMessage({ type: "success", text: "보증업체가 삭제되었습니다" });
    } catch {
      setMessage({ type: "error", text: "삭제에 실패했습니다" });
    }
  }, []);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-foreground-800">보증업체 관리</h2>
          <p className="text-xs text-foreground-600 mt-0.5">총 {companies.length}개</p>
        </div>

        <button
          onClick={showForm ? resetForm : openCreateForm}
          className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
            showForm
              ? "bg-background-200 text-foreground-600 hover:bg-background-300"
              : "bg-primary-500 text-foreground-50 hover:bg-primary-600"
          }`}
        >
          <span className="w-3.5 h-3.5 flex items-center justify-center">
            <i className={showForm ? "ri-close-line" : "ri-add-line"} />
          </span>
          {showForm ? "취소" : "업체 추가"}
        </button>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`px-4 py-3 rounded-lg text-xs font-medium ${
            message.type === "success"
              ? "bg-accent-100/80 text-accent-800 border border-accent-300/60"
              : "bg-red-500/10 text-red-400 border border-red-500/30"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-background-100 rounded-xl border border-background-300 p-5">
          <h3 className="text-sm font-bold text-foreground-800 mb-4">
            {editCompany ? "보증업체 수정" : "새 보증업체 등록"}
          </h3>

          <div className="space-y-5">
            {/* Row 1: 기본 정보 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">사이트 이름 *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="업체명을 입력하세요"
                  className="w-full bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">사이트 주소</label>
                <input
                  type="text"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://"
                  className="w-full bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">가입코드</label>
                <input
                  type="text"
                  value={registrationCode}
                  onChange={(e) => setRegistrationCode(e.target.value)}
                  placeholder="가입코드를 입력하세요"
                  className="w-full bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
                />
              </div>
            </div>

            {/* Row 2: 보증금 + 카테고리 + 정렬순서 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">보증금</label>
                <input
                  type="text"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  placeholder="예: 5억원"
                  className="w-full bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">카테고리</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors cursor-pointer"
                >
                  {CATEGORY_OPTIONS.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">정렬 순서</label>
                <input
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  min="0"
                  className="w-24 bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
                />
              </div>
            </div>

            {/* Row 3: 게임종류 */}
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-2">게임종류</label>
              <div className="flex flex-wrap gap-2">
                {GAME_TYPE_OPTIONS.map((gameType) => (
                  <button
                    key={gameType}
                    type="button"
                    onClick={() => toggleGameType(gameType)}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      gameTypes.includes(gameType)
                        ? "bg-primary-500 text-foreground-50"
                        : "bg-background-200 text-foreground-600 hover:bg-background-300"
                    }`}
                  >
                    {gameTypes.includes(gameType) && (
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-check-line" />
                      </span>
                    )}
                    {gameType}
                  </button>
                ))}
              </div>
            </div>

            {/* Row 4: 특장점 + 썸네일 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">특장점</label>
                <textarea
                  value={features}
                  onChange={(e) => setFeatures(e.target.value)}
                  placeholder="예: 먹튀이력 없음, 빠른 환전, 다양한 이벤트 등 특장점을 입력하세요"
                  rows={3}
                  maxLength={500}
                  className="w-full bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors resize-none"
                />
              </div>

              {/* 썸네일 이미지 업로드 */}
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">업체 썸네일 이미지</label>
                <div className="flex items-center gap-3">
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-background-200 border border-background-300 flex items-center justify-center flex-shrink-0">
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="썸네일 미리보기"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : (
                      <span className="w-8 h-8 flex items-center justify-center text-foreground-600">
                        <i className="ri-image-line text-xl" />
                      </span>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-background-200 text-foreground-600 text-xs font-medium rounded-lg hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                    >
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-upload-cloud-2-line" />
                      </span>
                      {uploading ? "업로드 중..." : "이미지 업로드"}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <input
                      type="text"
                      value={imageUrl}
                      onChange={(e) => {
                        setImageUrl(e.target.value);
                        setPreviewUrl(e.target.value);
                      }}
                      placeholder="또는 이미지 URL 직접 입력"
                      className="w-full bg-background-200 border border-background-300 rounded-lg px-3 py-2 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 5: 본문 */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-foreground-700">본문</label>
                <button
                  onClick={() => contentFileInputRef.current?.click()}
                  disabled={uploadingContent}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-background-200 text-foreground-600 text-[11px] font-medium rounded-lg hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-image-add-line" />
                  </span>
                  {uploadingContent ? "업로드 중..." : "본문 이미지 추가"}
                </button>
                <input
                  ref={contentFileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  onChange={handleContentImageUpload}
                  className="hidden"
                />
              </div>
              <textarea
                ref={contentTextareaRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="업체 상세 본문을 입력하세요. HTML 태그를 사용할 수 있습니다. (예: <b>볼드</b>, <br/>줄바꿈)&#10;&#10;'본문 이미지 추가' 버튼으로 이미지를 삽입할 수 있습니다."
                rows={8}
                className="w-full bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors resize-y font-mono"
              />
              <p className="text-[10px] text-foreground-600 mt-1">
                HTML 태그 사용 가능. "본문 이미지 추가" 버튼으로 이미지를 삽입하세요. 이미지는 커서 위치에 추가됩니다.
              </p>

              {/* 본문 미리보기 */}
              {content && (
                <div className="mt-3 p-4 bg-background-200 rounded-lg border border-background-300">
                  <span className="text-[10px] text-foreground-600 mb-2 block">본문 미리보기</span>
                  <div
                    className="prose prose-sm max-w-none text-sm text-foreground-700 [&_img]:rounded-lg [&_img]:my-3 [&_img]:max-w-full"
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                </div>
              )}
            </div>

            {/* Save button */}
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full py-2.5 bg-primary-500 text-foreground-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              {saving ? "저장 중..." : editCompany ? "수정하기" : "등록하기"}
            </button>
          </div>
        </div>
      )}

      {/* Companies List */}
      <div className="bg-background-100 rounded-xl border border-background-300 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : companies.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-shield-check-line text-xl text-foreground-600" />
            </div>
            <p className="text-sm text-foreground-600">등록된 보증업체가 없습니다</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-300 bg-background-200/50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">썸네일</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">업체명</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap hidden md:table-cell">사이트 주소</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap hidden lg:table-cell">가입코드</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap hidden lg:table-cell">보증금</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap hidden xl:table-cell">게임종류</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">상태</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">관리</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr
                    key={company.id}
                    className="border-b border-background-300 last:border-0 hover:bg-background-200/40 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-background-200 flex items-center justify-center">
                        {company.image_url ? (
                          <img
                            src={company.image_url}
                            alt={company.name}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = "none";
                            }}
                          />
                        ) : (
                          <span className="w-4 h-4 flex items-center justify-center text-foreground-600">
                            <i className="ri-building-line" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium text-foreground-700 whitespace-nowrap">{company.name}</span>
                      {company.category && (
                        <span className="block text-[10px] text-foreground-600 mt-0.5">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-900">{company.category}</span>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {company.website_url ? (
                        <a
                          href={company.website_url}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="text-xs text-primary-500 hover:text-primary-400 truncate max-w-[180px] block"
                        >
                          {company.website_url}
                        </a>
                      ) : (
                        <span className="text-xs text-foreground-600">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center hidden lg:table-cell">
                      <span className="text-xs text-foreground-600 whitespace-nowrap font-mono">
                        {company.registration_code || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center hidden lg:table-cell">
                      <span className="text-xs font-medium text-foreground-700 whitespace-nowrap">
                        {company.deposit_amount || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell">
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {company.game_types ? (
                          company.game_types.split(",").map((gt) => (
                            <span
                              key={gt}
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] bg-background-200 text-foreground-600 whitespace-nowrap"
                            >
                              {gt.trim()}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-foreground-600">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleToggleActive(company)}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors whitespace-nowrap cursor-pointer ${
                          company.is_active
                            ? "bg-accent-100/80 text-accent-800"
                            : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className={company.is_active ? "ri-eye-line" : "ri-eye-off-line"} />
                        </span>
                        {company.is_active ? "활성" : "비활성"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => openEditForm(company)}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-background-200 hover:bg-background-300 text-foreground-600 transition-colors cursor-pointer"
                        >
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-edit-line" />
                          </span>
                        </button>
                        <button
                          onClick={() => handleDelete(company)}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors cursor-pointer"
                        >
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-delete-bin-line" />
                          </span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}